import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useStudentProfileStore } from '../../stateManagers/stores/studentProfileStore';
import EnrolledCourseChips, { EnrolledCourse } from './parts/EnrolledCourseChips';
import ViewPracticalMaterialModal from './parts/ViewPracticalMaterialModal';

/* ─── Design Tokens (matches StudyMaterials.tsx) ─────────────────────── */
const C = {
  navy: '#0e225d', navyMid: '#1a3370',
  gold: '#fbb21f', goldDim: 'rgba(251,178,31,0.14)', goldBorder: 'rgba(251,178,31,0.35)',
  white: '#ffffff', border: '#e5e7eb', bg: '#f5f7fa', muted: '#9ca3af', sub: '#6b7280',
};

/* ─── Types (match PHP formatPracticalMaterialForStudent) ────────────── */
export interface PracticalFile {
  file_name: string; url: string; size: number; mime_type: string;
}
export interface PracticalVideo {
  video_id: string; original_name: string | null; status: string; m3u8_url: string | null; size: number | null;
}
export interface PracticalMaterial {
  id: string;
  course_id: string;
  module_id: string | null;
  chapter_id: string | null;
  scope_type: 'chapter' | 'course';
  owner_level: 'institute' | 'branch' | null;
  title: string;
  body: string;
  order: number;
  files: PracticalFile[];
  video_links: string[];
  videos: PracticalVideo[];
  files_count: number;
  videos_count: number;
  links_count: number;
  created_at: string;
}

interface Chapter {
  id: string; title: string; order: number;
  practical_materials: PracticalMaterial[];
  practical_materials_count: number;
}
interface Module {
  id: string; title: string; order: number;
  chapters: Chapter[]; chapters_count: number;
}

const StudentPracticalMaterials: React.FC = () => {
  const { profile, isLoading: profileLoading, error: profileError, fetchProfile } = useStudentProfileStore();
  const studentId = profile?.student_id ?? '';

  useEffect(() => {
    if (!profile && !profileLoading) fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  // One chip per course, even if the student has several enrollments (batches) in it
  const courses = useMemo<EnrolledCourse[]>(() => {
    const seen = new Set<string>();
    return (profile?.enrollments ?? [])
      .filter((e) => e.course_id && !seen.has(e.course_id) && seen.add(e.course_id))
      .map((e) => ({
        course_id: e.course_id as string,
        name: e.course_name ?? e.course_title ?? '',
        title: e.course_title ?? e.course_name ?? '',
        status: 'active',
      }));
  }, [profile]);

  const [selectedCourseId, setSelectedCourseId] = useState<string>('');
  const [generalMaterials, setGeneralMaterials] = useState<PracticalMaterial[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [selectedMaterial, setSelectedMaterial] = useState<PracticalMaterial | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Ignore responses from a course the student has already switched away from.
  const requestId = useRef(0);

  const resetContent = () => {
    setModules([]); setGeneralMaterials([]); setTotalCount(0); setError(null);
  };

  const fetchStructure = async (courseId: string) => {
    const mine = ++requestId.current;
    try {
      setLoading(true); setError(null);
      const response = await apiClient.get(`/v1/student/practical-materials/${studentId}/course/${courseId}/structure`);
      if (mine !== requestId.current) return;
      if (response.data.success) {
        const data = response.data.data;
        const mods: Module[] = data.modules || [];
        setModules(mods);
        setGeneralMaterials(data.general_materials || []);
        setTotalCount(data.total_count ?? 0);
        setExpandedModules(new Set(mods[0] ? [mods[0].id] : []));
      } else {
        setError('Failed to load practical materials');
      }
    } catch (err: any) {
      if (mine !== requestId.current) return;
      setError(err.response?.data?.error || 'Failed to load practical materials');
    } finally {
      if (mine === requestId.current) setLoading(false);
    }
  };

  const selectCourse = (courseId: string) => {
    setSelectedCourseId(courseId);
    if (courseId) {
      fetchStructure(courseId);
    } else {
      requestId.current++;
      setLoading(false);
      resetContent();
    }
  };

  const handleCourseSelect = (course: EnrolledCourse) =>
    selectCourse(course.course_id === selectedCourseId ? '' : course.course_id);

  // A student with a single course doesn't need an extra click.
  useEffect(() => {
    if (courses.length === 1 && !selectedCourseId) selectCourse(courses[0].course_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses]);

  const toggleModule = (id: string) =>
    setExpandedModules(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const handleView = (material: PracticalMaterial) => {
    setSelectedMaterial(material);
    setShowDetailsModal(true);
  };

  if (!profile) {
    return profileError ? (
      <div style={{ padding: '20px' }}>
        <Message severity="error" text={profileError} style={{ fontSize: '0.88rem' }} />
      </div>
    ) : (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
        <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" animationDuration=".8s" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

      {/* ── Course Filter (enrolled courses only) ─────────────────────── */}
      <div style={{ background: C.white, borderRadius: '14px', padding: '14px 18px', boxShadow: '0 1px 4px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '3px', height: '16px', background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '3px' }} />
          <span style={{ fontSize: '0.78rem', fontWeight: '700', color: C.navy, letterSpacing: '0.3px', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <i className="pi pi-filter" style={{ fontSize: '0.75rem' }} /> My Courses
          </span>
          {selectedCourseId && (
            <button
              onClick={() => selectCourse('')}
              style={{ marginLeft: 'auto', padding: '5px 10px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', color: C.sub, fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}
            >
              <i className="pi pi-times" style={{ fontSize: '0.68rem' }} /> Clear
            </button>
          )}
        </div>
        <EnrolledCourseChips
          courses={courses}
          selectedCourseId={selectedCourseId}
          onCourseSelect={handleCourseSelect}
        />
      </div>

      {error && <Message severity="error" text={error} style={{ fontSize: '0.88rem' }} />}

      {/* ── Empty / Loading / Content ─────────────────────────────────── */}
      {!selectedCourseId ? (
        <EmptyBlock icon="pi-briefcase" title="Select a Course" subtitle="Choose one of your courses above to see its practical materials" />
      ) : loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px', background: C.white, borderRadius: '14px', border: `1px solid ${C.border}` }}>
          <div style={{ textAlign: 'center' }}>
            <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" animationDuration=".8s" />
            <p style={{ marginTop: '14px', color: C.navy, fontSize: '0.9rem', fontWeight: '500' }}>Loading practical materials…</p>
          </div>
        </div>
      ) : error ? null : totalCount === 0 ? (
        <EmptyBlock icon="pi-inbox" title="Nothing here yet" subtitle="No practical materials have been shared for this course" />
      ) : (
        <>
          <div style={{ background: C.white, borderRadius: '14px', padding: '14px 18px', boxShadow: '0 1px 4px rgba(14,34,93,0.06)', border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '3px', height: '16px', background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '3px' }} />
            <span style={{ fontSize: '0.82rem', fontWeight: '700', color: C.navy }}>Practical Materials</span>
            <span style={{ fontSize: '0.75rem', color: C.muted, fontWeight: '500' }}>· {totalCount} total</span>
          </div>

          {/* General materials */}
          {generalMaterials.length > 0 && (
            <div style={{ background: C.white, borderRadius: '14px', padding: '16px 18px', boxShadow: '0 1px 4px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
              <SectionLabel title={`General (${generalMaterials.length})`} hint="for the whole course" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                {generalMaterials.map((m) => (
                  <MaterialCard key={m.id} material={m} positionLabel={`G.${m.order}`} onView={() => handleView(m)} />
                ))}
              </div>
            </div>
          )}

          {/* Module > Chapter > Materials */}
          {modules.length > 0 && (
            <div style={{ background: C.white, borderRadius: '14px', padding: '16px 18px', boxShadow: '0 1px 4px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
              {modules.map((module) => {
                const open = expandedModules.has(module.id);
                return (
                  <div key={module.id} style={{ marginBottom: '10px' }}>
                    <button
                      onClick={() => toggleModule(module.id)}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '10px', cursor: 'pointer', textAlign: 'left' }}
                    >
                      <i className={`pi ${open ? 'pi-chevron-down' : 'pi-chevron-right'}`} style={{ fontSize: '0.7rem', color: C.navy }} />
                      <div style={{ width: '28px', height: '28px', borderRadius: '7px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: '700', color: C.gold, flexShrink: 0 }}>
                        {module.order}
                      </div>
                      <span style={{ fontSize: '0.88rem', fontWeight: '700', color: C.navy }}>{module.title}</span>
                      <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: C.muted }}>
                        {module.chapters_count} chapter{module.chapters_count !== 1 ? 's' : ''}
                      </span>
                    </button>

                    {open && (
                      <div style={{ paddingLeft: '30px', marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {module.chapters.map((chapter) => (
                          <div key={chapter.id} style={{ border: `1px solid ${C.border}`, borderRadius: '10px', overflow: 'hidden' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 12px', background: C.white }}>
                              <span style={{ fontSize: '0.82rem', fontWeight: '600', color: C.navy }}>{module.order}.{chapter.order} {chapter.title}</span>
                              <span style={{ fontSize: '0.7rem', color: C.muted }}>({chapter.practical_materials_count})</span>
                            </div>
                            <div style={{ borderTop: `1px solid ${C.border}`, padding: '10px', background: C.bg, display: 'flex', flexDirection: 'column', gap: '7px' }}>
                              {chapter.practical_materials.map((m) => (
                                <MaterialCard key={m.id} material={m} positionLabel={`${module.order}.${chapter.order}.${m.order}`} onView={() => handleView(m)} />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <ViewPracticalMaterialModal
        visible={showDetailsModal}
        material={selectedMaterial}
        onHide={() => setShowDetailsModal(false)}
      />
    </div>
  );
};

/* ─── Sub-components ─────────────────────────────────────────────────── */

const EmptyBlock: React.FC<{ icon: string; title: string; subtitle: string }> = ({ icon, title, subtitle }) => (
  <div style={{ padding: '60px 20px', textAlign: 'center', background: C.white, borderRadius: '14px', border: `2px dashed ${C.border}` }}>
    <div style={{ width: '48px', height: '48px', background: 'rgba(14,34,93,0.06)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
      <i className={`pi ${icon}`} style={{ fontSize: '1.4rem', color: C.navy, opacity: 0.3 }} />
    </div>
    <h3 style={{ margin: '0 0 6px 0', color: C.navy, fontSize: '0.95rem', fontWeight: '700' }}>{title}</h3>
    <p style={{ margin: 0, color: C.muted, fontSize: '0.83rem' }}>{subtitle}</p>
  </div>
);

const SectionLabel: React.FC<{ title: string; hint?: string }> = ({ title, hint }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
    <div style={{ width: '3px', height: '14px', background: `linear-gradient(180deg, ${C.gold} 0%, #e09a00 100%)`, borderRadius: '3px' }} />
    <span style={{ fontSize: '0.8rem', fontWeight: '700', color: C.navy }}>{title}</span>
    {hint && <span style={{ fontSize: '0.72rem', color: C.muted }}>— {hint}</span>}
  </div>
);

const MaterialCard: React.FC<{ material: PracticalMaterial; positionLabel: string; onView: () => void }> = ({ material, positionLabel, onView }) => {
  const hasBody = !!material.body && material.body.trim() !== '' && material.body.trim() !== '.';

  return (
    <div
      onClick={onView}
      style={{ padding: '12px 14px', background: C.white, border: `1px solid ${C.border}`, borderRadius: '9px', cursor: 'pointer', transition: 'all 0.15s ease' }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateX(3px)'; e.currentTarget.style.borderColor = C.goldBorder; e.currentTarget.style.boxShadow = '0 2px 8px rgba(14,34,93,0.07)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateX(0)'; e.currentTarget.style.borderColor = C.border; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 2px 6px rgba(14,34,93,0.18)', fontSize: '0.55rem', fontWeight: '700', color: C.gold, letterSpacing: '-0.3px', lineHeight: 1 }}>
          {positionLabel}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <h5 style={{ margin: '0 0 3px 0', fontSize: '0.83rem', fontWeight: '600', color: C.navy }}>{material.title}</h5>
          {hasBody && (
            <p style={{ margin: '0 0 7px 0', fontSize: '0.75rem', color: C.sub, lineHeight: '1.45', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {material.body}
            </p>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', fontSize: '0.67rem', color: C.muted }}>
            {material.files_count > 0 && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '3px', color: C.navy, fontWeight: '600' }}>
                <i className="pi pi-paperclip" style={{ fontSize: '0.6rem' }} />
                {material.files_count} {material.files_count === 1 ? 'file' : 'files'}
              </span>
            )}
            {material.videos_count > 0 && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '3px', color: '#b8860b', fontWeight: '600' }}>
                <i className="pi pi-video" style={{ fontSize: '0.6rem' }} />
                {material.videos_count} {material.videos_count === 1 ? 'video' : 'videos'}
              </span>
            )}
            {material.links_count > 0 && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '3px', color: C.sub, fontWeight: '600' }}>
                <i className="pi pi-link" style={{ fontSize: '0.6rem' }} />
                {material.links_count} {material.links_count === 1 ? 'link' : 'links'}
              </span>
            )}
          </div>
        </div>

        <i className="pi pi-chevron-right" style={{ fontSize: '0.7rem', color: C.navy, opacity: 0.35, alignSelf: 'center' }} />
      </div>
    </div>
  );
};

export default StudentPracticalMaterials;